import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Heart, LogOut, Loader2, ChevronRight, Sparkles, Bell, BellOff, Star, MapPin } from "lucide-react";
import { useConsumerAuth } from "@/contexts/ConsumerAuthContext";
import { useFavoriteStores } from "@/hooks/useFavoriteStores";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { ProductCard } from "@/components/ProductCard";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function Account() {
  const { user, loading, refreshFavorites, favoriteCount } = useConsumerAuth();
  const [favorites, setFavorites] = useState([]);
  const [favLoading, setFavLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setFavLoading(true);
    refreshFavorites().then((items) => {
      setFavorites(items || []);
      setFavLoading(false);
    });
  }, [user, refreshFavorites]);

  if (loading) {
    return (
      <div className="container-narrow py-20 text-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </div>
    );
  }

  if (!user) return <SignInScreen />;

  return <SignedInScreen favorites={favorites} loading={favLoading} />;
}

// --------------------------------------------------------------------------
// Signed-out state
// --------------------------------------------------------------------------
const SignInScreen = () => {
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const { login, register, beginGoogle, favoriteCount } = useConsumerAuth();
  const [busy, setBusy] = useState(false);

  return (
    <div className="container-narrow py-12 md:py-20">
      <div className="max-w-md mx-auto">
        <div className="eyebrow mb-2">Your account</div>
        <h1 className="font-serif text-4xl mb-3">Keep your finds.</h1>
        <p className="text-foreground/70 mb-8 text-sm">
          Browsing is free and anonymous. Sign in only when you want to save
          favorites across devices.
          {favoriteCount > 0 && (
            <>
              {" "}
              You already have{" "}
              <strong className="text-foreground">
                {favoriteCount} saved
              </strong>{" "}
              on this device — sign in to keep them safe.
            </>
          )}
        </p>

        <Button
          data-testid="google-signin-btn"
          onClick={beginGoogle}
          className="w-full h-12 rounded-full bg-foreground text-background hover:bg-foreground/90"
        >
          <GoogleGlyph className="w-4 h-4 mr-2" />
          Continue with Google
        </Button>
        <div
          data-testid="google-privacy-tip"
          className="mt-3 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] p-3 text-[11px] leading-relaxed text-foreground/75"
        >
          <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300/85 mb-1">
            Heads up · privacy tip
          </div>
          Google will pass whatever <strong>name is on your Google account</strong>
          when you sign in. Bud Link only uses aliases everywhere on the site — but
          if you&apos;d rather we never see your real name at all, either use{" "}
          <strong>Continue with email</strong> below, or create a burner Gmail with
          your alias as the display name.
        </div>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-border" />
          <div className="eyebrow">or use email</div>
          <div className="flex-1 h-px bg-border" />
        </div>

        <EmailPasswordForm
          mode={mode}
          busy={busy}
          setBusy={setBusy}
          onLogin={login}
          onRegister={register}
        />

        <div className="text-center mt-6 text-sm">
          {mode === "login" ? (
            <>
              New here?{" "}
              <button
                data-testid="auth-switch-register"
                onClick={() => setMode("register")}
                className="text-primary hover:underline"
              >
                Create an account
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button
                data-testid="auth-switch-login"
                onClick={() => setMode("login")}
                className="text-primary hover:underline"
              >
                Sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const EmailPasswordForm = ({ mode, busy, setBusy, onLogin, onRegister }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password required");
      return;
    }
    setBusy(true);
    try {
      if (mode === "register") {
        if (password.length < 8) {
          toast.error("Password must be at least 8 characters");
          setBusy(false);
          return;
        }
        await onRegister({ email, password, name });
      } else {
        await onLogin({ email, password });
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {mode === "register" && (
        <Field label="Your name (optional)">
          <Input
            data-testid="auth-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Sam"
            className="h-11"
          />
        </Field>
      )}
      <Field label="Email">
        <Input
          data-testid="auth-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="h-11"
        />
      </Field>
      <Field label="Password">
        <Input
          data-testid="auth-password"
          type="password"
          autoComplete={mode === "register" ? "new-password" : "current-password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={mode === "register" ? "At least 8 characters" : "Your password"}
          className="h-11"
        />
      </Field>
      <Button
        type="submit"
        data-testid="auth-submit"
        disabled={busy}
        className="w-full h-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : mode === "register" ? (
          "Create account"
        ) : (
          "Sign in"
        )}
      </Button>
    </form>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const GoogleGlyph = ({ className }) => (
  <svg viewBox="0 0 48 48" className={className}>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.3 2.4-5.2 0-9.7-3.4-11.3-8l-6.5 5C9.6 39.7 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4 5.6l6.2 5.2C40.6 36.5 44 30.7 44 24c0-1.2-.1-2.3-.4-3.5z"/>
  </svg>
);

// --------------------------------------------------------------------------
// Signed-in state
// --------------------------------------------------------------------------
const SignedInScreen = ({ favorites, loading }) => {
  const { user, signOut } = useConsumerAuth();
  const navigate = useNavigate();
  const first = user.username
    ? `@${user.username}`
    : (user.email?.split("@")[0] || "you");

  return (
    <div className="container-narrow py-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <Avatar user={user} />
          <div>
            <div className="eyebrow">Signed in as</div>
            <div className="font-serif text-3xl">{first}</div>
            <div className="text-xs text-muted-foreground">{user.email}</div>
          </div>
        </div>
        <Button
          data-testid="account-signout-btn"
          variant="outline"
          onClick={() => {
            signOut();
            navigate("/");
          }}
          className="rounded-full bg-transparent border-border h-10 px-5"
        >
          <LogOut className="w-3.5 h-3.5 mr-2" />
          Sign out
        </Button>
      </div>

      <NotificationsCard />

      <FavoriteStoresSection />

      <div className="border border-border bg-card rounded-2xl p-6 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Heart className="w-4 h-4 text-rose-300" fill="currentColor" />
            <div>
              <div className="eyebrow">Your saved products</div>
              <div className="text-sm text-foreground/70">
                {favorites.length === 0
                  ? "No favorites yet — tap the heart on any product."
                  : `${favorites.length} item${favorites.length === 1 ? "" : "s"} saved`}
              </div>
            </div>
          </div>
          <Link
            to="/search"
            data-testid="account-browse-btn"
            className="pill"
          >
            <Sparkles className="w-3 h-3" /> Browse products
            <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground py-10">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </div>
      ) : favorites.length === 0 ? (
        <EmptyFavorites />
      ) : (
        <div
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
          data-testid="account-favorites-grid"
        >
          {favorites.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
};

const FavoriteStoresSection = () => {
  const { count, toggle } = useFavoriteStores();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const t = localStorage.getItem("bud_consumer_token");
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/consumer/favorites/stores`,
          { headers: t ? { Authorization: `Bearer ${t}` } : {} },
        );
        const j = await r.json();
        if (!live) return;
        setStores(j.stores || []);
      } catch {
        if (live) setStores([]);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [count]);

  return (
    <div
      className="border border-border bg-card rounded-2xl p-6 mb-6"
      data-testid="account-favorite-stores"
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Star
            className="w-4 h-4 text-amber-300"
            fill="currentColor"
            strokeWidth={1.5}
          />
          <div>
            <div className="eyebrow">Your favorite stores</div>
            <div className="text-sm text-foreground/70">
              {stores.length === 0
                ? "Tap the ★ on any dispensary to save it here."
                : `${stores.length} store${stores.length === 1 ? "" : "s"} saved`}
            </div>
          </div>
        </div>
        <Link
          to="/dispensaries"
          data-testid="account-browse-stores-btn"
          className="pill"
        >
          <MapPin className="w-3 h-3" /> Browse dispensaries
          <ChevronRight className="w-3 h-3" />
        </Link>
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground py-6">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </div>
      ) : stores.length === 0 ? null : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {stores.map((s) => (
            <Link
              key={s.slug}
              to={`/dispensaries/${s.slug}`}
              data-testid={`fav-store-${s.slug}`}
              className="group flex items-center gap-3 border border-border rounded-xl px-3 py-3 hover:border-foreground/30 hover:bg-secondary/30 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-amber-400/15 border border-amber-400/30 flex items-center justify-center shrink-0">
                <Star
                  className="w-4 h-4 text-amber-300"
                  fill="currentColor"
                  strokeWidth={1.5}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-serif text-base truncate">{s.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {s.city ? `${s.city}, ${s.state || "PA"}` : s.address}
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggle(s.slug, s.name);
                }}
                data-testid={`fav-store-remove-${s.slug}`}
                aria-label="Remove from favorites"
                className="text-foreground/50 hover:text-rose-300 text-[11px] uppercase tracking-[0.18em] shrink-0"
              >
                Remove
              </button>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Avatar = ({ user }) => {
  const label = user.username || user.email || "you";
  if (user.picture)
    return (
      <img
        src={user.picture}
        alt={label}
        className="w-14 h-14 rounded-full object-cover border border-border"
      />
    );
  const initials = (user.username || user.email || "?")
    .split(/[\s@._\-]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join("");
  return (
    <div className="w-14 h-14 rounded-full bg-primary/15 text-primary font-serif text-xl flex items-center justify-center border border-primary/30">
      {initials}
    </div>
  );
};

const EmptyFavorites = () => (
  <div className="border border-dashed border-border bg-card/40 rounded-2xl py-16 text-center">
    <Heart className="w-8 h-8 mx-auto text-muted-foreground/60 mb-3" />
    <div className="font-serif text-2xl mb-2">Nothing saved yet</div>
    <p className="text-sm text-foreground/60 max-w-sm mx-auto mb-5">
      Tap the heart on any product card to keep it here for next time.
    </p>
    <Link
      to="/search"
      className="pill pill-primary"
      data-testid="empty-fav-search"
    >
      Start exploring <ChevronRight className="w-3 h-3" />
    </Link>
  </div>
);

const NotificationsCard = () => {
  const { supported, permission, subscribed, loading, enable, disable, sendTest } =
    usePushNotifications();
  const isOff = !subscribed || permission !== "granted";
  return (
    <div
      data-testid="notifications-card"
      className="border border-border bg-card rounded-2xl p-6 mb-6"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {isOff ? (
            <BellOff className="w-4 h-4 text-foreground/50" />
          ) : (
            <Bell className="w-4 h-4 text-emerald-300" fill="currentColor" />
          )}
          <div>
            <div className="eyebrow">Push notifications</div>
            <div className="text-sm text-foreground/70">
              {!supported
                ? "Your browser doesn't support push (try Chrome or installing Bud Link to your home screen)"
                : subscribed
                ? "On — we'll ping you when a favorited product drops in price or comes back in stock, only at the shop you favorited it from."
                : "Get pinged when a favorited product drops in price or comes back in stock — only at the shops near you that you actually saved."}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {supported && subscribed && (
            <Button
              data-testid="notif-test-btn"
              variant="outline"
              onClick={sendTest}
              className="rounded-full h-10 px-5 bg-transparent border-border"
            >
              Send test
            </Button>
          )}
          {supported && (
            <Button
              data-testid="notif-toggle-btn"
              onClick={subscribed ? disable : enable}
              disabled={loading}
              className={`rounded-full h-10 px-5 ${
                subscribed
                  ? "bg-transparent border border-border text-foreground hover:bg-foreground/5"
                  : "bg-primary text-primary-foreground hover:bg-primary/90"
              }`}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : subscribed ? (
                <BellOff className="w-3.5 h-3.5 mr-2" />
              ) : (
                <Bell className="w-3.5 h-3.5 mr-2" />
              )}
              {subscribed ? "Turn off" : "Enable"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

